import React, { useMemo, useState } from 'react'
import { EmailFrame } from '../../../../marketing-v2/features/email-composer/components/EmailHtml'
import { renderEmail } from '../../../../marketing-v2/features/email-composer/email/render'
import { blocksFromWords } from '../../../../marketing-v2/features/plans/data/emailFor'
import { previewSubstitute, ownBookingLink } from '../../../../marketing-v2/features/sms-composer/smsUtils'
import { hapticTick } from '../../../../../_utils/haptics'
import { useBrand } from '../useBeu'
import { Tick, Mail } from './icons'
import { CAMPAIGNS_PATH } from './EmailWidget'
import '../skills.css'

// Words a draft should never go out with: a stand-in name or a made-up link
// the model typed into the message itself instead of a merge tag (checked on
// the saved words, before the preview fills the tags in)
const PLACEHOLDER = /\bclient x\b|\byoursalon\b|beu\.app\/book\/|\[(?:name|salon|link|date|time)\]|\bxx+\b/i
export const placeholdersIn = (draft) => {
  if (!draft) return false
  const words = [draft.subject, draft.title, draft.message].concat((draft.emailBlocks || []).map(b => JSON.stringify(b.content || {}))).join(' ')
  return PLACEHOLDER.test(words)
}

const Bubble = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
  </svg>
)

// A campaign Bailey drafted with its own tool (the server's 'draft' card),
// shown in the chat as the client will get it, with the way in to finish it.
// Bailey's drafts are often words only (a subject and a message, no design):
// those are drawn in the salon's template with the words in, exactly as the
// email editor opens them (blocksFromWords), so nobody has to go elsewhere to
// see what was made. Used by the Bailey tab and the desk pet.
// Wrapped in .bw-reply (where the widget colours live), so the card looks the
// same where it's drawn on its own (a draft opened from the Bailey home)
const Scope = ({ children }) => <div className="bw-reply">{children}</div>

const DraftWidget = (props) => <Scope><DraftBody {...props} /></Scope>

const DraftBody = ({ draft, loading, error, revised = false, compact = false, go, onRetry }) => {
  const brand = useBrand()
  const [whole, setWhole] = useState(false)
  const isText = !!(draft && draft.type === 'sms')
  const html = useMemo(() => {
    if (!draft || isText) return ''
    let blocks = Array.isArray(draft.emailBlocks) ? draft.emailBlocks : []
    if (!blocks.length && draft.message) {
      try {
        blocks = blocksFromWords({ subject: draft.subject, message: draft.message, audienceSegment: draft.audienceSegment }, { market: brand.market, kit: brand.spec, links: brand.links })
      } catch (e) { blocks = [] }
    }
    if (!blocks.length) return ''
    try { return renderEmail(blocks, { subject: draft.subject || '', sample: { FirstName: 'Sophie', SalonName: brand.salonName || 'Your salon' } }) } catch (e) { return '' }
  }, [draft, isText, brand.market, brand.spec, brand.links, brand.salonName])

  if (loading) {
    return <div className="bw-card bw-draft" role="status"><p className="bw-line">Opening the draft…</p></div>
  }
  if (error || !draft) {
    return (
      <div className="bw-card bw-draft">
        <p className="bw-title">That draft wouldn’t open</p>
        <p className="bw-line">It may have been sent or deleted since.</p>
        {onRetry && <div className="bw-actions"><button type="button" className="bw-btn is-secondary" onClick={onRetry}>Try again</button></div>}
      </div>
    )
  }

  const to = draft.opensAt && draft.opensAt.path
    ? draft.opensAt
    : { path: CAMPAIGNS_PATH, state: { openCampaignId: draft.id } }
  const open = () => { hapticTick(); go(to.path, to.state || {}) }
  const people = draft.cost && draft.cost.recipientCount != null ? draft.cost.recipientCount : draft.audienceSize
  const who = [draft.audienceName, people != null ? `${people} ${people === 1 ? 'person' : 'people'}` : null].filter(Boolean).join(' · ')
  // The salon's own booking page in the preview, as the text card shows it
  // ("Client X" stays: the composer's deliberate stand-in for a first name)
  const booking = ownBookingLink(brand.links)
  const sms = isText && draft.message ? (previewSubstitute(booking ? draft.message.split('{{BookingLink}}').join(booking) : draft.message, brand.salonName) || draft.message) : ''
  // Nobody to send it to yet (a new stylist, nobody opted in): say so,
  // rather than offer a send that reaches no one
  const nobody = people === 0
  const fixWords = placeholdersIn(draft)

  return (
    <div className={`bw-card bw-draft ${isText ? 'is-text' : 'is-email'} is-done`} role="group" aria-label={draft.title || 'Draft'}>
      <div className="bw-head">
        <span className="bw-kind">{isText ? <Bubble /> : <Mail />}{isText ? 'Text' : 'Email'}</span>
        <span className="bw-status is-done"><Tick size={12} />{revised ? 'Draft updated' : 'Draft saved'}</span>
      </div>
      <div className="bw-subject">
        <p className="bw-title">{(!isText && draft.subject) || draft.title || 'Draft'}</p>
        {who && <p className="bw-line">{who}</p>}
      </div>
      {html && (
        <div className={`bw-email${whole ? ' is-whole' : ''}`}>
          <EmailFrame html={html} width={640} clipHeight={whole ? undefined : (compact ? 260 : 460)} title={`Email: ${draft.subject || 'preview'}`} />
          <button type="button" className="bw-email-more" onClick={() => { hapticTick(); setWhole(w => !w) }}>{whole ? 'Show less' : 'See it all'}</button>
        </div>
      )}
      {sms && <p className="bw-sms">{sms}{draft.messageTruncated ? '…' : ''}</p>}
      {fixWords && <p className="bw-need" role="alert">This has a stand-in name or link in it. Change it before it goes out.</p>}
      {nobody && <p className="bw-hint">Nobody can get this yet: none of {isText ? 'the people it goes to' : 'your clients'} have said yes to marketing. Posts are the way to reach new people for now.</p>}
      <div className="bw-actions">
        <button type="button" className="bw-btn is-primary" onClick={open}>{nobody || fixWords ? 'Review it' : 'Review and send'}</button>
      </div>
      <p className="bw-foot">Nothing goes out until you send it.</p>
    </div>
  )
}

export default DraftWidget
